import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare, Image as ImageIcon, Clapperboard, Sparkles, Loader2, Download, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import ChatStudio from "@/components/ChatStudio";

const MEDIA = [
  { id: "image", label: "Image", icon: ImageIcon, endpoint: "/v1/images/generations", ph: "Décrivez l'image à générer…" },
  { id: "video", label: "Vidéo", icon: Clapperboard, endpoint: "/v1/videos/generations", ph: "Décrivez la vidéo à générer…" },
];

export default function Studio() {
  const { refreshCredits } = useOutletContext();
  const [tab, setTab] = useState("text");
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("crerea-vision");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/models").then((r) => setModels(r.data)).catch(() => {}); }, []);

  const mediaTab = MEDIA.find((m) => m.id === tab);
  const typeModels = models.filter((m) => m.type === tab);
  const current = models.find((m) => m.id === modelId);
  const cost = current?.cost ?? (tab === "image" ? 5 : 25);

  const switchTab = (id) => {
    setTab(id); setMedia(null); setError("");
    if (id !== "text") { const first = models.find((m) => m.type === id); if (first) setModelId(first.id); }
  };

  const runMedia = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setMedia(null); setError("");
    try {
      const { data } = await api.post(mediaTab.endpoint, { model: modelId, prompt });
      setMedia({ type: tab, url: data.data?.[0]?.url });
      refreshCredits?.();
    } catch (e) {
      const s = e.response?.status;
      const msg = s === 402 ? "Crédits insuffisants — rechargez votre solde."
        : s === 503 ? "Capacité de génération momentanément indisponible. Réessayez dans un instant."
          : (e.response?.data?.detail || "Échec de la génération");
      setError(msg); toast.error(msg, { duration: 8000 });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="studio-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">CRÉRÉA · Studio</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">Testez tous les modèles en direct</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {[{ id: "text", label: "Chat", icon: MessageSquare }, ...MEDIA].map((t) => (
          <button key={t.id} onClick={() => switchTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${tab === t.id ? "border-gold-500 bg-gold-500/15 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "text" ? (
        <ChatStudio kind="chat" onCredits={refreshCredits} placeholder="Écrivez votre demande… (raisonnement, rédaction, idées)" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4 rounded-2xl border border-gold-500/25 bg-card p-5">
            <div>
              <label className="mb-1.5 block font-mono text-xs uppercase tracking-wider text-muted-foreground">Modèle</label>
              <div className="flex flex-wrap gap-2">
                {typeModels.map((m) => (
                  <button key={m.id} onClick={() => setModelId(m.id)} data-testid={`model-select-${m.id}`}
                    className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${modelId === m.id ? "border-gold-500 bg-gold-500/10 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    {m.label} <span className="ml-2 font-mono text-xs opacity-70">{m.cost} cr.</span>
                  </button>
                ))}
              </div>
              {current && <p className="mt-2 text-xs text-muted-foreground">{current.desc}</p>}
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-xs uppercase tracking-wider text-muted-foreground">Prompt</label>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="studio-prompt-textarea" rows={5}
                placeholder={mediaTab.ph} className="w-full resize-none rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm outline-none focus:border-gold-500/60" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Coût : <span className="font-mono text-gold-400">{cost} crédits</span></span>
              <Button onClick={runMedia} disabled={loading || !prompt.trim()} data-testid="studio-generate-button" className="rounded-full bg-gold-500 text-black hover:bg-gold-400">
                {loading ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Génération…</> : <><Sparkles className="mr-1 h-4 w-4" />Générer</>}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5" data-testid="studio-output">
            <div className="mb-3 flex items-center justify-between border-b border-border pb-3">
              <span className="font-display font-semibold">Résultat</span>
              {current && <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground"><Gauge className="h-3 w-3" />{current.latency}</span>}
            </div>
            {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="studio-error">{error}</div>}
            <div className="grid min-h-[240px] place-items-center">
              {loading && <Loader2 className="h-6 w-6 animate-spin text-gold-400" data-testid="studio-loading" />}
              {!loading && !media && !error && <span className="text-sm text-muted-foreground">L'aperçu s'affichera ici.</span>}
              {media?.type === "image" && media.url && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full">
                  <img src={media.url} alt="Résultat" className="w-full rounded-xl" data-testid="result-image" />
                  <a href={media.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-gold-400"><Download className="h-4 w-4" />Ouvrir / télécharger</a>
                </motion.div>
              )}
              {media?.type === "video" && media.url && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full">
                  <video src={media.url} controls className="w-full rounded-xl" data-testid="result-video" />
                  <a href={media.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-gold-400"><Download className="h-4 w-4" />Ouvrir / télécharger</a>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
